import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, LogOut, RefreshCw, Copy, CheckCircle2 } from 'lucide-react';
import posLogo from '@/assets/posvision-logo.png';

interface MfaVerifyProps {
  onVerified: () => void;
  onSignOut: () => void;
}

export default function MfaVerify({ onVerified, onSignOut }: MfaVerifyProps) {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [factorId, setFactorId] = useState('');
  const [loading, setLoading] = useState(true);

  // Re-enroll state
  const [showReenroll, setShowReenroll] = useState(false);
  const [reenrolling, setReenrolling] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [newFactorId, setNewFactorId] = useState('');
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollVerifying, setEnrollVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadFactors();
  }, []);

  const loadFactors = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      const verifiedFactor = data.totp.find((f) => f.status === 'verified');
      if (verifiedFactor) {
        setFactorId(verifiedFactor.id);
      }
    } catch (err: any) {
      console.error('Error loading MFA factors:', err);
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }
    setVerifying(true);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyError) throw verifyError;

      onVerified();
    } catch (err: any) {
      toast.error('Code invalide. Veuillez réessayer.');
      setCode('');
    }
    setVerifying(false);
  };

  const handleReenroll = async () => {
    setReenrolling(true);
    setEnrollCode('');
    try {
      const { data: resetData, error: resetError } = await supabase.functions.invoke('reset-mfa');
      if (resetError) throw resetError;
      if (resetData?.error) throw new Error(resetData.error);

      toast.success('2FA réinitialisée. Reconnectez-vous pour générer un nouveau QR code.');
      await onSignOut();
    } catch (err: any) {
      toast.error('Erreur: ' + (err.message || ''));
    } finally {
      setReenrolling(false);
    }
  };

  const handleEnrollVerify = async () => {
    if (enrollCode.length !== 6) return;
    setEnrollVerifying(true);
    try {
      const { data: challenge, error: ce } = await supabase.auth.mfa.challenge({
        factorId: newFactorId,
      });
      if (ce) throw ce;
      const { error: ve } = await supabase.auth.mfa.verify({
        factorId: newFactorId,
        challengeId: challenge.id,
        code: enrollCode,
      });
      if (ve) throw ve;
      toast.success('2FA reconfigurée avec succès !');
      onVerified();
    } catch {
      toast.error('Code invalide. Veuillez réessayer.');
      setEnrollCode('');
    }
    setEnrollVerifying(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(224,50%,14%)] to-[hsl(224,55%,8%)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Re-enroll view with QR code
  if (showReenroll) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(224,50%,14%)] to-[hsl(224,55%,8%)] px-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="space-y-5 p-8">
            <div className="flex flex-col items-center gap-3">
              <img src={posLogo} alt="VisionPos 360" className="h-10 object-contain" />
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <ShieldCheck className="h-7 w-7 text-primary" />
              </div>
              <div className="space-y-1 text-center">
                <h2 className="text-lg font-bold text-foreground">Reconfigurer la 2FA</h2>
                <p className="text-xs text-muted-foreground">
                  Scannez ce QR code avec Google Authenticator ou une autre app TOTP.
                </p>
              </div>
            </div>

            {qrCode && (
              <div className="flex justify-center py-2">
                <img src={qrCode} alt="QR Code 2FA" className="h-48 w-48 rounded-lg border" />
              </div>
            )}

            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Ou entrez manuellement cette clé :</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded-md bg-muted px-3 py-2 font-mono text-xs tracking-wider">
                  {secret}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(secret);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="shrink-0"
                >
                  {copied ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Code à 6 chiffres</Label>
              <Input
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center font-mono text-2xl tracking-[0.5em]"
                maxLength={6}
                autoFocus
                data-allow-autofocus=""
                onKeyDown={(e) => e.key === 'Enter' && handleEnrollVerify()}
              />
            </div>

            <Button
              onClick={handleEnrollVerify}
              disabled={enrollVerifying || enrollCode.length !== 6}
              className="w-full gap-2"
            >
              {enrollVerifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4" />
              )}
              Activer la 2FA
            </Button>

            <Button
              variant="ghost"
              onClick={onSignOut}
              className="w-full gap-2 text-muted-foreground"
            >
              <LogOut className="h-4 w-4" /> Se déconnecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(224,50%,14%)] to-[hsl(224,55%,8%)] px-4">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardContent className="space-y-6 p-8">
          <div className="flex flex-col items-center gap-3">
            <img src={posLogo} alt="VisionPos 360" className="h-10 object-contain" />
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
            <div className="space-y-1 text-center">
              <h2 className="text-lg font-bold text-foreground">Vérification double facteur</h2>
              <p className="text-sm text-muted-foreground">
                Entrez le code généré par votre application d'authentification.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Code à 6 chiffres</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="text-center font-mono text-2xl tracking-[0.5em]"
              maxLength={6}
              autoFocus
              data-allow-autofocus=""
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
            />
          </div>

          <Button
            onClick={handleVerify}
            disabled={verifying || code.length !== 6}
            className="w-full gap-2"
          >
            {verifying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            Vérifier
          </Button>

          <Button
            variant="outline"
            onClick={handleReenroll}
            disabled={reenrolling}
            className="w-full gap-2 text-sm"
          >
            {reenrolling ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Reconfigurer la 2FA (nouveau QR code)
          </Button>

          <Button
            variant="ghost"
            onClick={onSignOut}
            className="w-full gap-2 text-muted-foreground"
          >
            <LogOut className="h-4 w-4" /> Se déconnecter
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
